import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/feature/AppShell";
import ProgressBar from "@/components/base/ProgressBar";
import { useCourseProgress } from "@/hooks/useCourseProgress";
import { computeRouteModules, computeRouteStats, MODULE_COURSE_ID } from "@/lib/route";
import { fetchMyQuizResult } from "@/services/quiz";

const accentBg: Record<string, string> = {
  primary: "bg-primary-500/15 text-primary-600",
  accent: "bg-accent-500/15 text-accent-600",
  secondary: "bg-secondary-500/15 text-secondary-600",
};

export default function MiRuta() {
  const navigate = useNavigate();
  const { progressByCourseId } = useCourseProgress();
  const [profileLabel, setProfileLabel] = useState("Completa tu test");
  const [temperament, setTemperament] = useState("Pendiente");

  useEffect(() => {
    let active = true;
    fetchMyQuizResult()
      .then((result) => {
        if (!active || !result) return;
        setProfileLabel(result.profile.label);
        setTemperament(result.profile.temperament);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Progreso real por módulo (combina el catálogo con el % real de Supabase
  // cuando hay un curso asociado a ese módulo). Misma lógica que usa Perfil
  // para que ambas pantallas coincidan.
  const modules = useMemo(() => computeRouteModules(progressByCourseId), [progressByCourseId]);
  const { completedModules, routeProgress } = useMemo(() => computeRouteStats(modules), [modules]);

  return (
    <AppShell headerVariant="onLight" showBack>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Perfil resumen */}
        <div className="bg-background-100 rounded-2xl border border-background-200 p-5 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-accent-500/15 flex items-center justify-center">
              <i className="ri-brain-line text-3xl text-accent-600"></i>
            </div>
            <div>
              <div className="text-xs text-foreground-500">Tu perfil</div>
              <div className="font-heading font-extrabold text-xl vv-gradient-text">{profileLabel}</div>
              <span className="inline-flex items-center gap-1 mt-2 text-[11px] bg-accent-500/10 text-accent-700 px-2.5 py-1 rounded-full">
                Temperamento base: {temperament}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 md:justify-end">
            <div className="flex-1 md:max-w-xs">
              <div className="text-xs text-foreground-500">Tu progreso en la ruta</div>
              <div className="text-2xl font-heading font-extrabold text-primary-600">{routeProgress}%</div>
              <div className="mt-1"><ProgressBar value={routeProgress} color="primary" /></div>
              <div className="text-[11px] text-foreground-500 mt-1">
                {completedModules} de {modules.length} módulos completados
              </div>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                <i className="ri-trophy-line text-2xl text-amber-500"></i>
              </div>
              <div className="text-[11px] text-foreground-600 mt-1 leading-tight">
                ¡Vas por<br />buen camino!
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mt-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-foreground-950">
              Mi Ruta <span className="vv-gradient-text">Vívelo</span>
            </h1>
            <p className="text-foreground-600 text-sm mt-1">
              Este es tu camino de crecimiento personalizado. Sigue avanzando paso a paso.
            </p>
          </div>
          <button className="hidden md:inline-flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-4 py-2 text-sm text-foreground-800 hover:border-primary-400 cursor-pointer whitespace-nowrap">
            <i className="ri-play-circle-line text-primary-600"></i> ¿Cómo funciona?
          </button>
        </div>

        {/* Modules timeline */}
        <ol className="mt-6 relative">
          {/* vertical line */}
          <div className="absolute left-4 md:left-5 top-4 bottom-4 w-px bg-background-300"></div>
          {modules.map((m) => {
            const statusPill =
              m.status === "completed" ? (
                <span className="inline-flex items-center gap-1 text-xs bg-primary-500/15 text-primary-700 px-2.5 py-1 rounded-full font-semibold">
                  Completado <i className="ri-check-line"></i>
                </span>
              ) : m.status === "progress" ? (
                <span className="text-xs bg-primary-500/10 text-primary-700 px-2.5 py-1 rounded-full font-semibold">
                  En progreso
                </span>
              ) : m.status === "next" ? (
                <span className="inline-flex items-center gap-1 text-xs bg-accent-500/15 text-accent-700 px-2.5 py-1 rounded-full font-semibold">
                  Próximo paso <i className="ri-star-fill"></i>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-background-200 text-foreground-600 px-2.5 py-1 rounded-full font-semibold">
                  <i className="ri-lock-2-line"></i> Bloqueado
                </span>
              );

            const dotClass =
              m.status === "completed"
                ? "bg-primary-500 text-background-950"
                : m.status === "progress"
                ? "bg-primary-500 text-background-950"
                : m.status === "next"
                ? "bg-accent-500 text-white"
                : "bg-background-300 text-foreground-600";

            const cardBorder =
              m.status === "next" ? "border-accent-500/50 ring-1 ring-accent-500/30" : "border-background-200";

            const courseId = MODULE_COURSE_ID[m.id];

            return (
              <li key={m.id} className="relative pl-12 md:pl-16 mb-4">
                <div className={`absolute left-0 top-4 w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${dotClass}`}>
                  {m.status === "completed" ? <i className="ri-check-line text-lg"></i> : m.id}
                </div>
                <button
                  onClick={() => {
                    if (m.status === "locked") return;
                    navigate(courseId ? `/app/cursos/${courseId}` : "/app/cursos");
                  }}
                  className={`w-full text-left bg-background-100 rounded-2xl border ${cardBorder} p-4 md:p-5 flex items-center gap-4 hover:border-primary-400 cursor-pointer transition-colors ${m.status === "locked" ? "opacity-90" : ""}`}
                >
                  <span className={`w-12 h-12 rounded-xl flex items-center justify-center ${accentBg[m.accent]}`}>
                    <i className={`${m.icon} text-2xl`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] tracking-wide font-semibold text-foreground-500">{m.number}</div>
                    <div className="font-heading font-bold text-foreground-950">{m.title}</div>
                    <div className="text-sm text-foreground-600">{m.description}</div>
                    <div className="flex sm:hidden flex-wrap items-center gap-2 mt-2">
                      {statusPill}
                      <span className="text-[11px] text-foreground-500">{m.progress}% completado</span>
                    </div>
                  </div>
                  <div className="hidden sm:flex flex-col items-end gap-1 min-w-[110px]">
                    {statusPill}
                    <div className="text-xs text-foreground-500">{m.progress}%</div>
                    <div className="w-24"><ProgressBar value={m.progress} color={m.status === "next" ? "accent" : "primary"} /></div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-400 text-xl"></i>
                </button>
              </li>
            );
          })}
        </ol>

        {/* Motivation message */}
        <div className="mt-4 relative overflow-hidden rounded-2xl bg-background-100 border border-background-200 p-5 md:p-6 flex items-start gap-4">
          <span className="w-10 h-10 rounded-full flex items-center justify-center bg-accent-500/15 text-accent-600">
            <i className="ri-sparkling-2-fill text-lg"></i>
          </span>
          <div className="flex-1">
            <div className="text-xs font-semibold text-accent-700">Mensaje para ti</div>
            <div className="text-sm text-foreground-800 mt-1">
              La consistencia es más importante que la velocidad.
            </div>
            <div className="font-heading font-bold text-foreground-950 mt-1">
              Sigue avanzando, tú puedes. 🚀
            </div>
          </div>
          <i className="ri-flag-2-fill text-3xl text-accent-500 hidden md:block"></i>
        </div>
      </div>
    </AppShell>
  );
}
